import { useState, useEffect } from "react";
import { Box, Typography, Button, IconButton, Slide } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import useAuth from "../hooks/useAuth";

// Styled components
const ToastContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: "100px", // Positioned lower on the page
  left: 0,
  right: 0,
  zIndex: 9999, // Higher z-index to appear above other elements
  display: "flex",
  justifyContent: "center",
  padding: "0 16px",
}));

const ToastContent = styled(Box)(({ theme }) => ({
  backgroundColor: "#E3F2FD", // Light blue background
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  padding: "12px 24px",
  display: "flex",
  alignItems: "center",
  maxWidth: "800px",
  width: "100%",
}));

export default function SurveyToast() {
  const [visible, setVisible] = useState(false);
  const { expireAccessDate, role } = useAuth();

  useEffect(() => {
    // Check if we should show the survey toast
    const checkSurveyEligibility = () => {
      // Only show for normal users, not admins
      if (!expireAccessDate || role === "ADMIN") return false;

      // Calculate activation date (expireAccessDate minus 6 months)
      const expiryDate = new Date(expireAccessDate);
      const activationDate = new Date(expiryDate);
      activationDate.setMonth(activationDate.getMonth() - 6);

      const currentDate = new Date();

      // Calculate the difference in months
      const monthDiff =
        (currentDate.getFullYear() - activationDate.getFullYear()) * 12 +
        (currentDate.getMonth() - activationDate.getMonth());

      // Show the toast if at least the configured number of months has passed
      const requiredMonths = parseInt(
        process.env.REACT_APP_SURVEY_TOAST_MONTHS || "1"
      );
      return monthDiff >= requiredMonths;
    };

    // Check if we should show the survey
    const shouldShowSurvey = checkSurveyEligibility();

    // Check if the user has already dismissed the survey today
    const lastDismissed = localStorage.getItem("surveyDismissedDate");
    const today = new Date().toDateString();
    const surveyDismissed = lastDismissed === today;

    // Only show if eligible and not dismissed
    setVisible(shouldShowSurvey && !surveyDismissed);
  }, [expireAccessDate, role]);

  const handleDismiss = () => {
    // Store the dismissal date in localStorage (so it shows again tomorrow)
    localStorage.setItem("surveyDismissedDate", new Date().toDateString());
    setVisible(false);
  };

  const handleTakeSurvey = () => {
    // Open the survey in a new tab using the URL from .env
    window.open(process.env.REACT_APP_SURVEY_URL, "_blank");
    // Dismiss the toast
    handleDismiss();
  };

  if (!visible) return null;

  return (
    <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
      <ToastContainer>
        <ToastContent>
          <InfoIcon
            sx={{
              color: "#2196F3",
              mr: 2,
              fontSize: "28px",
            }}
          />
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            Aiutaci a migliorare il servizio! Completa il nostro breve sondaggio
            anonimo sulla tua esperienza.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleTakeSurvey}
            sx={{
              mx: 2,
              minWidth: "180px",
              px: 3,
            }}
          >
            Vai alla Survey
          </Button>
          <IconButton size="small" onClick={handleDismiss} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </ToastContent>
      </ToastContainer>
    </Slide>
  );
}
